import { useCallback, useMemo } from 'react'

import { type Edge as ReactFlowEdge } from '@xyflow/react'

import { buildParentMap } from './treeUtils'
import { type CustomNode } from './types'

interface UseMultiSelectProps {
  nodes: CustomNode[]
  edges: ReactFlowEdge[]
  setNodes: React.Dispatch<React.SetStateAction<CustomNode[]>>
}

const useMultiSelect = ({ nodes, edges, setNodes }: UseMultiSelectProps) => {
  const checkedNodes = useMemo(() => nodes.filter((n) => n.data.checked), [nodes])

  const onUnselectAll = useCallback(() => {
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        data: {
          ...n.data,
          checked: false,
          multiselectDisabled: false,
        },
      }))
    )
  }, [setNodes])

  const onMultiSelectNode = useCallback(
    (nodeId: string, isChecked: boolean) => {
      const parentMap = buildParentMap(edges)

      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === nodeId) {
            // Only the clicked node gets checked/unchecked
            return {
              ...n,
              selectable: false,
              selected: false,
              data: {
                ...n.data,
                checked: isChecked,
                multiselectDisabled: false,
              },
            }
          }

          // Other nodes: disable when checking, enable when unchecking
          return {
            ...n,
            data: {
              ...n.data,
              checked: false,
              multiselectDisabled: isChecked ? true : !parentMap.has(n.id),
            },
          }
        })
      )
    },
    [edges, setNodes]
  )

  return { checkedNodes, onMultiSelectNode, onUnselectAll }
}

export default useMultiSelect
